import { useState, useEffect, useRef, useMemo } from "react";
import { X, ChevronDown, Search, Check } from "lucide-react";

/** Lightweight modal — escapes shadcn dialog complexity for fast iteration. */
export default function Modal({ open, onClose, title, children, size = "md", testId }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);
    if (!open) return null;
    const sizes = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm" onClick={onClose} data-testid={testId || "modal"}>
            <div onClick={e => e.stopPropagation()} className={`w-full ${sizes[size]} bg-muted border border-border rounded-xl max-h-[90vh] flex flex-col overflow-hidden`}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                    <div className="font-serif text-xl">{title}</div>
                    <button onClick={onClose} data-testid="modal-close" className="text-muted-foreground hover:text-foreground">
                        <X className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">{children}</div>
            </div>
        </div>
    );
}

export function Field({ label, children, hint }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 block">{label}</label>
            {children}
            {hint && <div className="text-xs text-muted-foreground mt-1.5">{hint}</div>}
        </div>
    );
}

export function Input(props) {
    return <input {...props}
        className={`w-full bg-background border border-border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-2.5 text-sm placeholder-muted-foreground ${props.className || ""}`} />;
}

export function Textarea(props) {
    return <textarea {...props}
        className={`w-full bg-background border border-border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary outline-none px-4 py-2.5 text-sm placeholder-muted-foreground ${props.className || ""}`} />;
}

/** Searchable dropdown. Drop-in replacement for native <select>:
 *  - `options` accepts `["a","b"]` OR `[{value,label}]`
 *  - emits a synthetic onChange({ target: { value, name } }) so existing form
 *    handlers keep working
 *  - search box appears at the top of the popup (per product spec)
 *  - Esc / outside-click close, ↑/↓/Enter navigate, type to filter
 *  - hidden input mirrors the value so HTML5 `required` still gates form submit
 */
export function Select({ options = [], value, onChange, placeholder = "Select…", required, disabled, name, className = "", ...rest }) {
    const norm = useMemo(() => options.map(o => typeof o === "string" ? { value: o, label: o } : o), [options]);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const ref = useRef(null);
    const searchRef = useRef(null);

    const selected = norm.find(o => String(o.value) === String(value));
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return norm;
        return norm.filter(o => String(o.label).toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q));
    }, [norm, query]);

    useEffect(() => {
        if (!open) return;
        setQuery("");
        setActiveIdx(0);
        // focus the search input on open
        const t = setTimeout(() => searchRef.current?.focus(), 30);
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => { clearTimeout(t); document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
    }, [open]);

    const choose = (opt) => {
        onChange?.({ target: { value: opt.value, name } });
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
        else if (e.key === "Enter") { e.preventDefault(); if (filtered[activeIdx]) choose(filtered[activeIdx]); }
    };

    const testId = rest["data-testid"];
    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => !disabled && setOpen(o => !o)}
                data-testid={testId}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={`w-full bg-background border rounded-lg ${open ? "border-primary ring-1 ring-primary" : "border-border"} px-3 py-2.5 text-sm text-left flex items-center justify-between gap-2 ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/60"}`}>
                <span className={`truncate ${selected ? "text-foreground" : "text-muted-foreground"}`}>{selected ? selected.label : placeholder}</span>
                <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={1.5}/>
            </button>
            <input type="hidden" name={name} value={value ?? ""} required={required && !selected}/>
            {open && (
                <div className="absolute z-40 mt-1 left-0 right-0 bg-background border border-border rounded-lg shadow-2xl shadow-black/50 overflow-hidden" data-testid={testId ? `${testId}-popup` : "select-popup"}>
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-background">
                        <Search className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5}/>
                        <input
                            ref={searchRef}
                            value={query}
                            onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
                            onKeyDown={onKeyDown}
                            placeholder="Search…"
                            data-testid={testId ? `${testId}-search` : "select-search"}
                            className="flex-1 bg-transparent outline-none text-sm placeholder-muted-foreground"
                        />
                        {query && (
                            <button type="button" onClick={() => setQuery("")} className="text-muted-foreground hover:text-foreground" aria-label="Clear">
                                <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
                            </button>
                        )}
                    </div>
                    <div role="listbox" className="max-h-60 overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-4 text-xs text-muted-foreground text-center" data-testid="select-no-results">No matches for "{query}"</div>
                        ) : filtered.map((o, i) => {
                            const isActive = i === activeIdx;
                            const isSelected = selected && String(selected.value) === String(o.value);
                            return (
                                <button
                                    type="button"
                                    key={o.value}
                                    onMouseEnter={() => setActiveIdx(i)}
                                    onClick={() => choose(o)}
                                    role="option"
                                    aria-selected={isSelected}
                                    data-testid={testId ? `${testId}-option-${o.value}` : `select-option-${o.value}`}
                                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${isActive ? "bg-muted" : ""} ${isSelected ? "text-primary" : "text-foreground"} hover:bg-muted`}>
                                    <span className="truncate">{o.label}</span>
                                    {isSelected && <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5}/>}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export function GoldButton({ children, testId, ...rest }) {
    return <button {...rest} data-testid={testId || rest["data-testid"]}
        className={`bg-primary text-primary-foreground hover:opacity-90 px-5 py-2.5 font-medium text-sm transition-opacity disabled:opacity-50 rounded-lg ${rest.className || ""}`}>
        {children}
    </button>;
}

export function GhostButton({ children, testId, ...rest }) {
    return <button {...rest} data-testid={testId || rest["data-testid"]}
        className={`border border-border hover:border-primary hover:text-primary px-5 py-2.5 text-sm transition-colors disabled:opacity-50 rounded-lg ${rest.className || ""}`}>
        {children}
    </button>;
}

export function StatusBadge({ status }) {
    const map = {
        active:    "bg-emerald-50 text-emerald-700 ring-emerald-200",
        open:      "bg-emerald-50 text-emerald-700 ring-emerald-200",
        scheduled: "bg-blue-50 text-blue-700 ring-blue-200",
        completed: "bg-slate-100 text-slate-600 ring-slate-200",
        suspended: "bg-rose-50 text-rose-700 ring-rose-200",
        trial:     "bg-primary/10 text-primary ring-primary/20",
        unpaid:    "bg-rose-50 text-rose-700 ring-rose-200",
        partial:   "bg-amber-50 text-amber-700 ring-amber-200",
        paid:      "bg-emerald-50 text-emerald-700 ring-emerald-200",
        closed:    "bg-slate-100 text-slate-600 ring-slate-200",
        sent:      "bg-emerald-50 text-emerald-700 ring-emerald-200",
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded-full ring-1 font-medium ${map[status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
            {status}
        </span>
    );
}

export function EmptyState({ title, subtitle, action }) {
    return (
        <div className="border border-dashed border-border py-20 text-center">
            <div className="font-serif text-2xl mb-2">{title}</div>
            {subtitle && <div className="text-sm text-muted-foreground mb-6">{subtitle}</div>}
            {action}
        </div>
    );
}

export function PageHeader({ title, subtitle, action }) {
    return (
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
                <div className="overline mb-3">Workspace</div>
                <h1 className="font-serif text-4xl font-light">{title}</h1>
                {subtitle && <p className="text-muted-foreground text-sm mt-2 max-w-2xl">{subtitle}</p>}
            </div>
            {action}
        </div>
    );
}
